import React, { useState, useEffect, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { Search, Award, ExternalLink, Paperclip } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';
import { supplierService, supplierCertificationService } from '../services/api';
import CertificationStatusBadge, { CERT_TYPES, CERT_STATUSES } from '../components/CertificationStatusBadge';

export default function SupplierCertificationsPage() {
  const [items, setItems] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({
    supplier_id: '',
    certification_type: '',
    status: '',
    expiry_from: '',
    expiry_to: '',
  });

  useEffect(() => { loadSuppliers(); }, []);
  useEffect(() => { load(); }, [filters]);

  const loadSuppliers = async () => {
    try {
      const r = await supplierService.getAll({ is_active: true });
      setSuppliers(r.data || []);
    } catch {}
  };

  const load = async () => {
    setLoading(true);
    try {
      const params = {};
      Object.keys(filters).forEach(k => { if (filters[k]) params[k] = filters[k]; });
      const r = await supplierCertificationService.list(params);
      setItems(r.data || []);
    } catch {
      toast.error('Errore caricamento');
    } finally {
      setLoading(false);
    }
  };

  const counts = useMemo(() => ({
    total: items.length,
    valida: items.filter(i => i.status === 'valida').length,
    in_scadenza: items.filter(i => i.status === 'in_scadenza').length,
    scaduta: items.filter(i => i.status === 'scaduta').length,
  }), [items]);

  const openAttachment = (url) => {
    if (!url) return;
    const API_URL = process.env.REACT_APP_BACKEND_URL;
    window.open(`${API_URL}${url}`, '_blank', 'noopener,noreferrer');
  };

  const reset = () => setFilters({ supplier_id: '', certification_type: '', status: '', expiry_from: '', expiry_to: '' });

  return (
    <div className="space-y-6" data-testid="supplier-certifications-page">
      {/* Header */}
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
            <Award className="w-6 h-6 text-slate-700" />
            Scadenze Certificazioni Fornitori
          </h1>
          <p className="text-sm text-slate-500 mt-1">Monitora certificazioni in scadenza o gia scadute della tua catena di fornitura</p>
        </div>
      </div>

      {/* Summary cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card>
          <CardContent className="p-5">
            <p className="text-xs text-slate-500 uppercase tracking-wider font-bold">Totale</p>
            <p className="text-3xl font-bold text-slate-900 mt-1">{counts.total}</p>
          </CardContent>
        </Card>
        <Card className="border-l-4 border-l-emerald-500">
          <CardContent className="p-5">
            <p className="text-xs text-emerald-600 uppercase tracking-wider font-bold">Valide</p>
            <p className="text-3xl font-bold text-emerald-600 mt-1">{counts.valida}</p>
          </CardContent>
        </Card>
        <Card className="border-l-4 border-l-amber-500">
          <CardContent className="p-5">
            <p className="text-xs text-amber-700 uppercase tracking-wider font-bold">In Scadenza</p>
            <p className="text-3xl font-bold text-amber-600 mt-1">{counts.in_scadenza}</p>
          </CardContent>
        </Card>
        <Card className="border-l-4 border-l-red-500">
          <CardContent className="p-5">
            <p className="text-xs text-red-600 uppercase tracking-wider font-bold">Scadute</p>
            <p className="text-3xl font-bold text-red-600 mt-1">{counts.scaduta}</p>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base flex items-center gap-2">
            <Search className="w-4 h-4" />
            Filtri
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-5 gap-3">
            <div className="space-y-1">
              <Label className="text-xs">Fornitore</Label>
              <Select value={filters.supplier_id || 'all'} onValueChange={(v) => setFilters(f => ({ ...f, supplier_id: v === 'all' ? '' : v }))}>
                <SelectTrigger data-testid="filter-supplier"><SelectValue placeholder="Tutti" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Tutti</SelectItem>
                  {suppliers.map(s => <SelectItem key={s.id} value={s.id}>{s.code} - {s.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1">
              <Label className="text-xs">Tipo Certificazione</Label>
              <Select value={filters.certification_type || 'all'} onValueChange={(v) => setFilters(f => ({ ...f, certification_type: v === 'all' ? '' : v }))}>
                <SelectTrigger data-testid="filter-type"><SelectValue placeholder="Tutti" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Tutti</SelectItem>
                  {CERT_TYPES.map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1">
              <Label className="text-xs">Stato</Label>
              <Select value={filters.status || 'all'} onValueChange={(v) => setFilters(f => ({ ...f, status: v === 'all' ? '' : v }))}>
                <SelectTrigger data-testid="filter-status"><SelectValue placeholder="Tutti" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Tutti</SelectItem>
                  {CERT_STATUSES.map(s => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1">
              <Label className="text-xs">Scadenza Dal</Label>
              <Input type="date" value={filters.expiry_from} onChange={(e) => setFilters(f => ({ ...f, expiry_from: e.target.value }))} data-testid="filter-expiry-from" />
            </div>
            <div className="space-y-1">
              <Label className="text-xs">Scadenza Al</Label>
              <Input type="date" value={filters.expiry_to} onChange={(e) => setFilters(f => ({ ...f, expiry_to: e.target.value }))} data-testid="filter-expiry-to" />
            </div>
          </div>
          <div className="mt-3 flex justify-end">
            <Button variant="outline" size="sm" onClick={reset} data-testid="reset-filters-btn">Reset filtri</Button>
          </div>
        </CardContent>
      </Card>

      {/* Table */}
      <Card>
        <CardContent className="p-0">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <div className="w-6 h-6 border-2 border-slate-900 border-t-transparent rounded-full animate-spin"></div>
            </div>
          ) : items.length === 0 ? (
            <div className="text-center py-12 text-slate-500">
              <Award className="w-12 h-12 text-slate-300 mx-auto mb-3" />
              <p>Nessuna certificazione corrisponde ai filtri selezionati</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b bg-slate-50">
                    <th className="px-3 py-2 text-left font-medium text-slate-600">Fornitore</th>
                    <th className="px-3 py-2 text-left font-medium text-slate-600">Tipo</th>
                    <th className="px-3 py-2 text-left font-medium text-slate-600">Numero</th>
                    <th className="px-3 py-2 text-left font-medium text-slate-600">Ente</th>
                    <th className="px-3 py-2 text-left font-medium text-slate-600">Emissione</th>
                    <th className="px-3 py-2 text-left font-medium text-slate-600">Scadenza</th>
                    <th className="px-3 py-2 text-left font-medium text-slate-600">Stato</th>
                    <th className="px-3 py-2 text-left font-medium text-slate-600">Allegato</th>
                    <th className="px-3 py-2"></th>
                  </tr>
                </thead>
                <tbody>
                  {items.map(c => (
                    <tr key={c.id} className="border-b last:border-0 hover:bg-slate-50" data-testid={`overview-cert-${c.id}`}>
                      <td className="px-3 py-2">
                        <Link to={`/suppliers/${c.supplier_id}`} className="text-sky-600 hover:text-sky-800 font-medium">
                          {c.supplier_name || c.supplier_code}
                        </Link>
                        <div className="font-mono text-xs text-slate-500">{c.supplier_code}</div>
                      </td>
                      <td className="px-3 py-2 font-medium">{c.certification_type}</td>
                      <td className="px-3 py-2 font-mono text-slate-700">{c.certificate_number || '-'}</td>
                      <td className="px-3 py-2">{c.certification_body || '-'}</td>
                      <td className="px-3 py-2">{c.issue_date || '-'}</td>
                      <td className="px-3 py-2 font-medium">{c.expiry_date || '-'}</td>
                      <td className="px-3 py-2"><CertificationStatusBadge status={c.status} days={c.days_to_expiry} /></td>
                      <td className="px-3 py-2">
                        {c.attachment_url ? (
                          <button onClick={() => openAttachment(c.attachment_url)} className="inline-flex items-center gap-1 text-sky-600 hover:text-sky-800">
                            <Paperclip className="w-3.5 h-3.5" />
                            <span className="truncate max-w-[140px]">{c.attachment_name || 'allegato'}</span>
                          </button>
                        ) : <span className="text-slate-400">-</span>}
                      </td>
                      <td className="px-3 py-2 text-right">
                        <Link to={`/suppliers/${c.supplier_id}`}>
                          <Button variant="ghost" size="sm" className="h-8" data-testid={`open-supplier-${c.supplier_id}`}>
                            <ExternalLink className="w-4 h-4" />
                          </Button>
                        </Link>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
